import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PublicNav, Footer } from "@/components/Layout";
import { toast, Toaster } from "sonner";
import { Calendar, Clock, Scissors, CheckCircle2, XCircle } from "lucide-react";

function todayIso() { return new Date().toISOString().slice(0, 10); }
function frDate(iso) {
  return iso ? new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "";
}

export default function ManageBooking() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [appt, setAppt] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);

  const reload = async () => {
    try { const { data } = await api.get(`/appointments/manage/${token}`); setAppt(data); }
    catch { setError("Rendez-vous introuvable. Vérifiez votre lien."); }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { const { data } = await api.get(`/appointments/manage/${token}`); if (!cancelled) setAppt(data); }
      catch { if (!cancelled) setError("Rendez-vous introuvable. Vérifiez votre lien."); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!editing || !date) return;
    let cancelled = false;
    (async () => {
      await Promise.resolve();
      if (cancelled) return;
      setLoadingSlots(true); setSlot("");
      try { const { data } = await api.get(`/appointments/manage/${token}/availability`, { params: { date } }); if (!cancelled) setSlots(data.slots); }
      catch { if (!cancelled) setSlots([]); }
      finally { if (!cancelled) setLoadingSlots(false); }
    })();
    return () => { cancelled = true; };
  }, [editing, date, token]);

  const reschedule = async () => {
    try {
      await api.patch(`/appointments/manage/${token}/reschedule`, { date, start_time: slot });
      toast.success("Rendez-vous modifié");
      setEditing(false); setDate(""); setSlot("");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const cancel = async () => {
    if (!window.confirm("Annuler définitivement ce rendez-vous ?")) return;
    try { await api.patch(`/appointments/manage/${token}/cancel`); toast.success("Rendez-vous annulé"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen bg-[#0A0908] text-[#F4F0EA]">
      <PublicNav />
      <Toaster theme="dark" />
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="text-[10px] tracking-[0.4em] text-[#C5A059] uppercase mb-2">Mon rendez-vous</div>
        <h1 className="font-serif text-5xl font-light mb-10">Gérer ma réservation.</h1>

        {error ? (
          <div className="border border-[#8C3131]/40 bg-[#8C3131]/10 p-6 text-[#E5B8B8]" data-testid="manage-error">{error}</div>
        ) : !appt ? (
          <p className="text-[#6B6760]">Chargement…</p>
        ) : (
          <div data-testid="manage-card">
            <div className={`p-6 border ${appt.status === "cancelled" ? "border-[#8C3131]/40 bg-[#8C3131]/5" : "border-[#2C2A26] bg-[#141311]"}`}>
              <div className="flex items-center gap-2 mb-6">
                {appt.status === "cancelled"
                  ? <><XCircle className="w-5 h-5 text-[#8C3131]" /> <span className="text-sm uppercase tracking-[0.2em] text-[#8C3131]" data-testid="manage-status">Annulé</span></>
                  : <><CheckCircle2 className="w-5 h-5 text-[#7CBF8E]" /> <span className="text-sm uppercase tracking-[0.2em] text-[#7CBF8E]" data-testid="manage-status">Confirmé</span></>}
              </div>
              <div className="space-y-3">
                <Row icon={<Scissors className="w-4 h-4" />} label="Prestation" value={`${appt.service_name} · ${appt.duration} min · ${appt.price}€`} />
                <Row icon={<Scissors className="w-4 h-4" />} label="Coiffeur" value={appt.staff_name || "—"} />
                <Row icon={<Calendar className="w-4 h-4" />} label="Date" value={frDate(appt.date)} />
                <Row icon={<Clock className="w-4 h-4" />} label="Heure" value={`${appt.start_time} — ${appt.end_time}`} />
              </div>
            </div>

            {appt.status !== "cancelled" && !editing && (
              <div className="flex flex-wrap gap-4 mt-6">
                <button onClick={() => { setEditing(true); setDate(appt.date); }} className="btn-primary" data-testid="manage-edit-btn">Modifier la date / l&apos;heure</button>
                <button onClick={cancel} className="btn-secondary text-[#E5B8B8] border-[#8C3131]/40 hover:border-[#8C3131]" data-testid="manage-cancel-btn">Annuler le rendez-vous</button>
              </div>
            )}

            {editing && (
              <div className="mt-8 p-6 border border-[#2C2A26] bg-[#141311]" data-testid="manage-reschedule">
                <h2 className="font-serif text-2xl font-light mb-6">Choisir un nouveau créneau</h2>
                <label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Date</label>
                <input type="date" min={todayIso()} value={date} onChange={(e) => setDate(e.target.value)} className="mt-2 mb-6 block bg-[#0A0908] border border-[#2C2A26] px-3 py-2 text-sm text-[#F4F0EA]" data-testid="manage-date" />
                {loadingSlots ? (
                  <p className="text-[#6B6760] text-sm">Recherche des disponibilités…</p>
                ) : slots.length === 0 ? (
                  <p className="text-[#6B6760] text-sm">Aucun créneau disponible ce jour-là.</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {slots.map((s) => (
                      <button key={s} onClick={() => setSlot(s)} className={`py-2 text-sm border transition ${slot === s ? "border-[#C5A059] bg-[#C5A059]/10 text-[#C5A059]" : "border-[#2C2A26] text-[#A39E93] hover:border-[#A39E93]"}`} data-testid={`manage-slot-${s}`}>{s}</button>
                    ))}
                  </div>
                )}
                <div className="flex gap-4 mt-8">
                  <button disabled={!slot} onClick={reschedule} className="btn-primary disabled:opacity-30 disabled:cursor-not-allowed" data-testid="manage-confirm-reschedule">Confirmer le changement</button>
                  <button onClick={() => { setEditing(false); setSlot(""); }} className="btn-secondary" data-testid="manage-cancel-edit">Retour</button>
                </div>
              </div>
            )}

            {appt.status === "cancelled" && (
              <button onClick={() => navigate("/reservation")} className="btn-primary mt-6" data-testid="manage-rebook-btn">Prendre un nouveau rendez-vous</button>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-[#A39E93] text-sm uppercase tracking-[0.15em]">{icon}{label}</span>
      <span className="text-right capitalize">{value}</span>
    </div>
  );
}
